/**
 * Selectors dérivés pour le flux de contractualisation emprunteur
 *
 * Ces selectors extraient les données de cardStatuses et permissions
 * du nouveau DTO BorrowerContractualizationResponse.
 */

import { useMemo } from 'react';
import {
  useBorrowerGlobalInfos,
  useBorrowerContractualizationStore,
  useBorrowerDocumentsFromStore,
} from './borrowerContractualizationStore';
import { CardState } from '../types/cardState';
import { GenericColors } from 'enums/genericColors';
import { mapApiStatusToCardState } from '../types/api';
import {
  BorrowerContractualizationResponse,
  BorrowerContractualizationResponseContractSignatureStatusEnum,
  BorrowerContractualizationResponseStatusCategoryEnum,
  CardStatusDto
} from 'api/quote';
import { BorrowerCardStatuses, BorrowerPermissions } from 'api/borrower';
import { SimulationDocumentDto } from 'api/documents';
import { sortDocuments } from '../hooks/useSortedDocuments';

// ============================================
// Helper: Accès aux données
// ============================================

/**
 * Hook interne pour accéder au globalInfos
 * Le type BorrowerContractualizationResponse inclut maintenant contractualizationData
 */
const useBorrowerData = (): BorrowerContractualizationResponse | null => {
  return useBorrowerGlobalInfos();
};

/**
 * Helper pour obtenir cardStatuses depuis contractualizationData
 */
const getCardStatuses = (
  data: BorrowerContractualizationResponse | null
): BorrowerCardStatuses | undefined => {
  return data?.cardStatuses;
};

/**
 * Helper pour obtenir permissions depuis contractualizationData
 */
const getPermissions = (
  data: BorrowerContractualizationResponse | null
): BorrowerPermissions | undefined => {
  return data?.permissions;
};

// ============================================
// Selectors: CardStatuses
// ============================================

/**
 * Retourne l'objet cardStatuses complet
 * Cherche dans contractualizationData (nouveau format) puis à la racine
 */
export const useBorrowerCardStatuses = (): BorrowerCardStatuses | undefined => {
  const data = useBorrowerData();
  return getCardStatuses(data);
};

/**
 * Statut de la carte Assuré
 * Fallback vers l'ancien comportement si cardStatuses n'existe pas
 */
export const useBorrowerInsuredCardStatus = (): CardState => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);

  return mapApiStatusToCardState(cardStatuses?.insured?.status);
};

/**
 * Statut de la carte Risque
 * Fallback vers l'ancien comportement si cardStatuses n'existe pas
 */
export const useBorrowerRiskCardStatus = (): CardState => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);

  // Si cardStatuses existe, utiliser le nouveau format
  if (cardStatuses?.risk) {
    return mapApiStatusToCardState(cardStatuses.risk.status);
  }

  // Fallback: calcul basé sur l'ancien format (isCompletedRiskInformation)
  if (data?.isCompletedRiskInformation) {
    return CardState.VALID;
  }
  return CardState.CRITICAL;
};

/**
 * Statut de la carte Conditions
 * Fallback vers l'ancien comportement si cardStatuses n'existe pas
 */
export const useBorrowerConditionsCardStatus = (): CardState => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);

  // Si cardStatuses existe, utiliser le nouveau format
  if (cardStatuses?.conditions) {
    return mapApiStatusToCardState(cardStatuses.conditions.status);
  }

  // Fallback: pas d'ancien format pour conditions, retourner CRITICAL par défaut
  return CardState.CRITICAL;
};

/**
 * Vrai si la carte Conditions Particulières est verrouillée par une règle métier
 * (par exemple tant qu'un bien immobilier n'est pas lié pour les projets immo,
 * ou tant qu'un devis est verrouillé).
 * Ce flag est distinct de `canEditRisk` qui représente une permission utilisateur.
 */
export const useBorrowerConditionsCardDisabled = (): boolean => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.conditions?.isDisabled ?? false;
};

/**
 * Vrai si la carte Bien Immobilier est verrouillée par une règle métier
 * (dès qu'un emprunteur du projet a envoyé sa DIA, le bien devient non modifiable
 * pour tous les emprunteurs du projet).
 */
export const useBorrowerRealEstateCardDisabled = (): boolean => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.realEstate?.isDisabled ?? false;
};

/**
 * Statut de la carte Bien Immobilier. Renvoie CRITICAL si pas encore défini.
 */
export const useBorrowerRealEstateCardStatus = (): CardState => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);

  if (cardStatuses?.realEstate) {
    return mapApiStatusToCardState(cardStatuses.realEstate.status);
  }
  return CardState.CRITICAL;
};

/**
 * Id du bien immobilier lié au projet d'emprunt (undefined si non défini).
 */
export const useBorrowerRealEstateId = (): string | undefined => {
  const data = useBorrowerData();
  return data?.realEstateId ?? undefined;
};

/**
 * Vrai si la carte Bien Immobilier doit être visible
 * (le backend ne la renvoie que pour les projets immobiliers).
 */
export const useIsBorrowerRealEstateCardVisible = (): boolean => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.realEstate != null;
};

/**
 * Statut de la carte Contrat
 * Fallback vers l'ancien comportement si cardStatuses n'existe pas
 */
export const useBorrowerContractCardStatus = (): CardState => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);

  // Si cardStatuses existe, utiliser le nouveau format
  if (cardStatuses?.contract) {
    return mapApiStatusToCardState(cardStatuses.contract.status);
  }

  // Fallback: retourner CRITICAL par défaut
  return CardState.CRITICAL;
};

// ============================================
// Selectors: Cartes spécifiques emprunteur (Process Tracker)
// ============================================

/**
 * Statut de la carte Demande d'Adhésion (DIA)
 */
export const useSubscriptionRequestCardStatus = (): CardState => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return mapApiStatusToCardState(cardStatuses?.subscriptionRequest?.status);
};

/**
 * Carte Demande d'Adhésion complète (avec isDisabled et message)
 */
export const useSubscriptionRequestCard = (): CardStatusDto | undefined => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.subscriptionRequest;
};

/**
 * Statut de la carte Certificat d'Adhésion (CIA)
 */
export const useSubscriptionCertificateCardStatus = (): CardState => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return mapApiStatusToCardState(cardStatuses?.subscriptionCertificate?.status);
};

/**
 * Carte Certificat d'Adhésion complète (avec isDisabled et message)
 */
export const useSubscriptionCertificateCard = (): CardStatusDto | undefined => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.subscriptionCertificate;
};

/**
 * Statut de la carte Questionnaire Santé (QS)
 * Retourne null si cas Lemoine (pas requis)
 */
export const useMedicalSurveyCardStatus = (): CardState | null => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  const cardStatus = cardStatuses?.medicalSurvey;

  if (cardStatus === null || cardStatus === undefined) {
    return null;
  }
  return mapApiStatusToCardState(cardStatus.status);
};

/**
 * Carte Questionnaire Santé complète (avec isDisabled et message)
 * Retourne undefined si cas Lemoine
 */
export const useMedicalSurveyCard = (): CardStatusDto | null | undefined => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.medicalSurvey;
};

/**
 * Statut de la carte Bon Pour Accord (BPA)
 * Retourne null si cas Lemoine (pas requis)
 */
export const useApprovedAgreementCardStatus = (): CardState | null => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  const cardStatus = cardStatuses?.approvedAgreement;

  if (cardStatus === null || cardStatus === undefined) {
    return null;
  }
  return mapApiStatusToCardState(cardStatus.status);
};

/**
 * Carte Bon Pour Accord complète (avec isDisabled et message)
 * Retourne undefined si cas Lemoine
 */
export const useApprovedAgreementCard = (): CardStatusDto | null | undefined => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.approvedAgreement;
};

/**
 * Statut de la carte Fiche médicale complémentaire (Mutlog uniquement,
 * grilles FM3..FM11). Retourne null si la carte n'est pas applicable au
 * dossier (G0/FM1/FM2/MNCAP/Lemoine → cardStatuses.medicalForm absent).
 */
export const useMedicalFormCardStatus = (): CardState | null => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  const cardStatus = cardStatuses?.medicalForm;

  if (cardStatus === null || cardStatus === undefined) {
    return null;
  }
  return mapApiStatusToCardState(cardStatus.status);
};

/**
 * Carte Fiche médicale complémentaire complète (status + isDisabled).
 * Retourne undefined si non applicable.
 */
export const useMedicalFormCard = (): CardStatusDto | null | undefined => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.medicalForm;
};

// ============================================
// Selectors: Permissions
// ============================================

/**
 * Retourne l'objet permissions complet
 */
export const useBorrowerPermissions = (): BorrowerPermissions | undefined => {
  const data = useBorrowerData();
  return getPermissions(data);
};

/**
 * Peut modifier l'assuré
 */
export const useCanEditBorrowerInsured = (): boolean => {
  const data = useBorrowerData();
  const permissions = getPermissions(data);
  return permissions?.canEditInsured ?? true;
};

/**
 * Peut modifier le risque
 */
export const useCanEditBorrowerRisk = (): boolean => {
  const data = useBorrowerData();
  const permissions = getPermissions(data);
  return permissions?.canEditRisk ?? true;
};

/**
 * Peut modifier les prêts du projet
 */
export const useCanEditBorrowerLoan = (): boolean => {
  const data = useBorrowerData();
  const permissions = getPermissions(data);
  return permissions?.canEditLoan ?? true;
};

/**
 * Peut modifier le questionnaire médical
 */
export const useCanSendMedicalSurvey = (): boolean => {
  const data = useBorrowerData();
  const permissions = getPermissions(data);
  return permissions?.canSendMedicalSurvey ?? false;
};

/**
 * Peut modifier le 
 */
export const useCanSendSubscriptionRequest = (): boolean => {
  const data = useBorrowerData();
  const permissions = getPermissions(data);
  return permissions?.canSendSubscriptionRequest ?? false;
};


// ============================================
// Selectors: Désactivation des cartes
// ============================================

/**
 * Vérifie si les cartes d'information (insured, risk, conditions) sont désactivées
 */
export const useBorrowerInfoCardsDisabled = (): boolean => {
  const data = useBorrowerData();
  const permissions = getPermissions(data);

  return useMemo(() => {
    if (!permissions) {
      return false;
    }
    // Si aucune permission d'édition, les cartes sont désactivées
    return !permissions.canEditRisk && !permissions.canEditInsured;
  }, [permissions]);
};

/**
 * Vérifie si la carte Demande d'Adhésion est désactivée
 */
export const useIsSubscriptionRequestDisabled = (): boolean => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.subscriptionRequest?.isDisabled ?? false;
};

/**
 * Vérifie si la carte Certificat d'Adhésion est désactivée
 */
export const useIsSubscriptionCertificateDisabled = (): boolean => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.subscriptionCertificate?.isDisabled ?? true;
};

/**
 * Message de désactivation pour le Certificat d'Adhésion
 */
export const useSubscriptionCertificateDisabledMessage = (): string | undefined => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.subscriptionCertificate?.message;
};

// ============================================
// Selectors: Couleurs des badges
// ============================================

/**
 * Convertit un CardState en couleur de badge
 */
export const cardStateToColor = (state: CardState): GenericColors => {
  switch (state) {
    case CardState.VALID:
      return GenericColors.Green;
    case CardState.IN_PROGRESS:
      return GenericColors.Yellow;
    case CardState.CRITICAL:
    default:
      return GenericColors.Red;
  }
};

/**
 * Couleur du badge pour la carte Demande d'Adhésion
 */
export const useSubscriptionRequestBadgeColor = (): GenericColors => {
  const status = useSubscriptionRequestCardStatus();
  return cardStateToColor(status);
};

/**
 * Couleur du badge pour la carte Certificat d'Adhésion
 */
export const useSubscriptionCertificateBadgeColor = (): GenericColors => {
  const status = useSubscriptionCertificateCardStatus();
  return cardStateToColor(status);
};

// ============================================
// Selectors: Visibilité des cartes
// ============================================

/**
 * Vérifie si la carte Questionnaire Santé doit être visible
 * (pas visible en cas Lemoine)
 */
export const useIsMedicalSurveyCardVisible = (): boolean => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  // La carte est visible si elle est présente dans cardStatuses (non null)
  return cardStatuses?.medicalSurvey != null;
};

/**
 * Vérifie si la carte Bon Pour Accord doit être visible
 * (pas visible en cas Lemoine)
 */
export const useIsApprovedAgreementCardVisible = (): boolean => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  // La carte est visible si elle est présente dans cardStatuses (non null)
  return cardStatuses?.approvedAgreement != null;
};

/**
 * Vérifie si la carte Réponse Questionnaire Médical doit être visible
 * (pas visible en cas Lemoine)
 */
export const useIsMedicalSurveyResponseCardVisible = (): boolean => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  // La carte est visible si elle est présente dans cardStatuses (non null)
  return cardStatuses?.medicalSurveyResponse != null;
};

export const useIsCertificateCardVisible = (): boolean => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  // La carte est visible si elle est présente dans cardStatuses (non null)
  return cardStatuses?.subscriptionCertificate != null;
};

/**
 * Vérifie si la carte Fiche médicale complémentaire doit être affichée.
 * Visible uniquement quand le back l'a explicitement émise (Mutlog FM3..FM11) ;
 * absente sinon (G0/FM1/FM2/MNCAP/Lemoine).
 */
export const useIsMedicalFormCardVisible = (): boolean => {
  const data = useBorrowerData();
  const cardStatuses = getCardStatuses(data);
  return cardStatuses?.medicalForm != null;
};

/**
 * Indique si le courtier peut déclencher l'envoi de la fiche médicale
 * complémentaire au souscripteur (selon les permissions back).
 * Faux par défaut tant que le back ne renvoie pas explicitement true.
 */
export const useCanSendMedicalForm = (): boolean => {
  const data = useBorrowerData();
  const permissions = getPermissions(data);
  return permissions?.canSendMedicalForm ?? false;
};

/**
 * Raison i18n de désactivation de l'envoi de la fiche médicale, telle
 * que renvoyée par le back (ex: "MEDICAL_SURVEY_NOT_SIGNED").
 * À utiliser comme message d'aide sous le bouton désactivé.
 */
export const useMedicalFormDisabledReason = (): string | undefined => {
  const data = useBorrowerData();
  return data?.disabledReasons?.canSendMedicalForm;
};

/**
 * Données de suivi (isSend / isReceived / resultsCount) pour la fiche
 * médicale complémentaire. undefined si la carte n'est pas active.
 */
export const useMedicalFormProcessInfo = () => {
  const data = useBorrowerData();
  return data?.process?.medicalForm;
};


// ============================================
// Selectors: États dérivés
// ============================================

/**
 * Vérifie si le processus de contractualisation a démarré
 */
export const useIsContractStepStarted = (): boolean => {
  const data = useBorrowerData();
  return data?.isContractStepStarted ?? false;
};

/**
 * Vérifie si c'est un cas Lemoine
 */
export const useIsCasLemoine = (): boolean => {
  const data = useBorrowerData();
  return data?.isCasLemoine ?? false;
};

/**
 * Vrai si le devis emprunteur est dans l'état "abandonné" (statusCode "AB").
 * Sert à figer l'UI : bannière en haut + désactivation des sections d'action
 * (cartes, tracker, documents) — voir BorrowerContractualizationStep.
 *
 * On s'aligne sur le code brut `statusCode === 'AB'` car c'est ce que le back
 * envoie en pratique ; on évite de coupler à statusCategory qui peut ne pas
 * être toujours rempli selon les routes.
 */
export const useIsBorrowerQuoteCancelled = (): boolean => {
  const data = useBorrowerData();
  return data?.statusCode === 'AB';
};

/**
 * Vrai si le devis emprunteur peut être abandonné : uniquement quand il s'agit
 * encore d'un devis "en cours". On masque donc l'action quand :
 *  - le devis est déjà abandonné (statusCategory "Cancelled"), ou
 *  - le contrat existe déjà : contrat signé (contractSignatureStatus "Signed")
 *    ou certificat d'adhésion déjà émis (hasCertificate).
 */
export const useCanAbandonBorrowerQuote = (): boolean => {
  const data = useBorrowerData();
  if (!data) return false;

  const isCancelled =
    data.statusCategory ===
    BorrowerContractualizationResponseStatusCategoryEnum.Cancelled;
  const isContractSigned =
    data.contractSignatureStatus ===
    BorrowerContractualizationResponseContractSignatureStatusEnum.Signed;
  const hasContract = isContractSigned || data.hasCertificate;

  return !isCancelled && !hasContract;
};

// ============================================
// Selectors: Documents
// ============================================

/**
 * Retourne les documents du borrower depuis le store
 * (récupérés via fetchBorrowerDocuments)
 */
export const useBorrowerDocuments = (): SimulationDocumentDto[] => {
  return useBorrowerDocumentsFromStore();
};

/**
 * Retourne les documents triés selon l'ordre défini
 */
export const useBorrowerSortedDocuments = (): SimulationDocumentDto[] => {
  const documents = useBorrowerDocuments();

  return useMemo(() => {
    return sortDocuments(documents);
  }, [documents]);
};

/**
 * Retourne l'état de récupération des documents
 */
export { useBorrowerIsFetchingDocuments } from './borrowerContractualizationStore';

/**
 * Vérifie si l'animation de complétion a été effectuée
 */
export const useBorrowerIsAnimationCompleted = (): boolean => {
  const data = useBorrowerData();
  return data?.isAnimationCompleted ?? false;
};

/**
 * Vérifie si le certificat d'adhésion est disponible
 */
export const useBorrowerHasCertificate = (): boolean => {
  const data = useBorrowerData();
  return data?.hasCertificate ?? false;
};

/**
 * Configuration email pour l'envoi de documents borrower
 */
export const useBorrowerEmailConfig = () => {
  const data = useBorrowerData();
  const borrowerId = useBorrowerContractualizationStore(state => state.borrowerId);

  return useMemo(() => {
    if (!data?.insured) return undefined;

    return {
      risqueAssureId: borrowerId,
      sendTo: data.insured.email
        ? [
            {
              email: data.insured.email,
              name: data.insured.displayName || undefined
            }
          ]
        : undefined
    };
  }, [data?.insured, borrowerId]);
};
